import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, useEffect, useState, useCallback } from 'react';
import { Formik } from 'formik';
import { useLocation } from 'react-router-dom';
import { EditSignerDialogueHandler, DialogueHandlerProps } from './types';
import EditSignerContext, { EditSignerCtx } from './EditSignerContext';
import useStorage from '@/hooks/useStorage';
import usePostEditSigner from '@/hooks/useApi/usePostEditSigner';
import { usePartnerData } from '@/providers/PartnerData.provider';
import buildEditSignerRows from './buildEditSignerRows.helper';
import EditSignerDialogue from './EditSignerDialogue';

const { Box, Button, Title, Table, TextField } = vp;

type FC = FunctionComponent;

const EditSignercontainer: FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const signer = {
    ID: queryParams.get('ID') || '',
    FullName: decodeURIComponent(queryParams.get('FullName') || ''),
    DOB: decodeURIComponent(queryParams.get('DOB') || ''),
    Occupation: decodeURIComponent(queryParams.get('Occupation') || ''),
    EmailAddress: queryParams.get('EmailAddress') || '',
    HomeAddress: decodeURIComponent(queryParams.get('HomeAddress') || ''),
    DocumentSigned: queryParams.get('DocumentSigned') || '',
    DateAdded: queryParams.get('DateAdded') || '',
  };
  const SigningAuthorityID = signer.ID || '';
  const hasAddressLine2 = signer.HomeAddress?.split(/, /).length > 5;
  const editSignerRows = buildEditSignerRows();
  const SigningAuthority = decodeURIComponent(queryParams.get('signers') || '');
  const {
    signerCreation: { storeSignerCreation },
    accountToken: { getAccountToken },
  } = useStorage();
  const accountToken = getAccountToken();

  const [shouldEditSigner, setShouldEditSigner] = useState(false);
  const [showEditSignerErrorAlert, setShowEditSignerErrorAlert] = useState(false);
  const [editSigner, postEditSigner] = usePostEditSigner({ bearer: accountToken || '' });

  const EditSigner: EditSignerCtx = {
    showEditSignerErrorAlert,
  };

  const defaultValues = {
    AuthorizedFullLegalName: signer.FullName,
    AuthorizedEmailAddress: signer.EmailAddress,
    SigningAuthorityID,
    AuthorizedOccupation: signer.Occupation,
    AuthorizedDOB: signer.DOB,
  };

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<EditSignerDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: EditSignerDialogueHandler = {
        error: {
          back: {
            default: () => {},
          },
          confirm: {
            default: () => {
              setShowEditSignerErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
    onEditSigner: (value: vp.EditSignerRequest) => {
      postEditSigner(value);
      setShouldEditSigner(true);
    },
  };

  useEffect(() => {
    if (shouldEditSigner) {
      setShouldEditSigner(false);
      if (editSigner.Success === false) {
        setTimeout(() => {
          setShowEditSignerErrorAlert(true);
        }, 1500);
      }
    }
  }, [shouldEditSigner]);

  useEffect(() => {
    if (editSigner.Success) {
      storeSignerCreation('signerChange');
      history.back();
    }
  }, [editSigner.Success]);

  const getForms = useCallback(
    () => (
      <Formik
        validateOnMount
        initialValues={defaultValues}
        onSubmit={handlers.onEditSigner}
        validationSchema={createdValidationSchema()}
      >
        {({ values, handleChange, handleBlur, handleSubmit, touched, errors }) => {
          const editSignerTable: vp.TableProps = {
            name: 'edit-signer-fields',
            variant: 'pair',
            columns: {
              Field: { header: '' },
              Input: { header: '' },
            },
            rows: editSignerRows.map(({ label, id, preset }) => ({
              Field: label,
              Input: (
                <TextField
                  key={id}
                  name={id}
                  variant="standard"
                  value={values[id]}
                  preset={preset}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={errors[id] && touched[id] ? errors[id] : null}
                />
              ),
            })),
          };

          return (
            <form onSubmit={handleSubmit}>
              <Table {...editSignerTable} />
              <Box mt="lg" jc="fs">
                <Button
                  id="back-to-signers-btn"
                  label="Save"
                  color="primary"
                  width="223px"
                  type="submit"
                  isDisabled={isRoleViewOnly}
                  noShadow
                  round
                />
              </Box>
            </form>
          );
        }}
      </Formik>
    ),
    [hasAddressLine2],
  );

  return (
    <EditSignerContext.Provider value={EditSigner}>
      <Box mt="lg" mb="lg" flx="col">
        <Title title="Edit Signer" />
      </Box>
      {getForms()}
      <EditSignerDialogue handleDialogue={handlers.handleDialogue} />
    </EditSignerContext.Provider>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      AuthorizedFullLegalName: Yup.string()
        .required('Please fill out all mandatory fields')
        .matches(/^[A-Za-z0-9 ]+$/, "This field cannot contain any of special characters. eg) ? ! / < ~ ` ' > <"),
      AuthorizedOccupation: Yup.string()
        .required('Please fill out all mandatory fields')
        .matches(/^[A-Za-z0-9 ]+$/, "This field cannot contain any of special characters. eg) ? ! / < ~ ` ' > <"),
      AuthorizedEmailAddress: Yup.string()
        .matches(
          /^(.*)@(?!(gmail|mail|email|usa|myself|consultant|europe|post|asia|iname|writeme|dr|cheerful|accountant|techie|engineer|linuxmail|musician|workmail|financier|fastservice|contractor|planetmail|homemail|hackermail|dbzmail|coolsite|australiamail|pacificwest|nycmail|kissfans|technologist|webname|null|greenmail|instructor|secratery|salesperson|repairman|deliveryman|allergist|execs|auctioneer|bartender|comic|yahoo|hotmail|msn|protonmail|zohomail|outlook|icloud|aol|tutanota|startmail|hushmail|mailfence|aweber|countermail|posteo|mail2one|mail2world|mail2usa|mail2seattle|mail2washington|mail2cool|mail2art|mail2fun|mail2dude|mail2expert|mail2consultant|thunderbird|yandex|ranren|mailasia|gmx|xwebtools|netscape|fexpost)).(.*)/,
          'Please provide a valid Email Address',
        )
        .test('email-validation', 'The email address already exists.', (value) => {
          const newSignerValidation = SigningAuthority.split(',').includes(value || '');

          return newSignerValidation && value !== defaultValues.AuthorizedEmailAddress
            ? false
            : newSignerValidation && value === defaultValues.AuthorizedEmailAddress
              ? true
              : true;
        })
        .required('Please fill out all mandatory fields')
        .email('Please provide a valid Email Address'),
    });
  }
};

export default EditSignercontainer;
